// Reports view — "Average over / under by day of week".
// Complements the existing "Adherence by day of week" chart (unsigned
// magnitude, blended protein+carbs+kcal): this one shows a SIGNED average
// deviation for a single metric at a time, so the coach can see WHICH WAY a
// weekday tends to miss, not just how far. Owns its own metric-toggle state
// (rather than ReportsView.js computing a fixed series and handing it down,
// like the sibling charts) since the toggle, the series it drives, and the
// axis it rescales all have to stay in lockstep — keeping that in one place
// avoids a 3-way prop dance for what is really one piece of state.

import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  BarChart,
  Bar,
  Rectangle,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

import { CHART_THEME, translateMacroLabel, translateMacroUnit } from "./viewTokens";
import { TooltipShell } from "./viewAtoms";
import { MetricToggle } from "./MetricToggle";
import { overUnderAxisMax, buildOverUnderSeries, OVER_UNDER_METRICS } from "../../../../../utils/nutrition-progress";
import { useCenteredTooltip } from "../../../../../hooks/useCenteredTooltip";

const ro = (t, key, opts) => t(`nutritionPage.progress.reports.overUnderChart.${key}`, opts);

const BAR_COLOR = "var(--tracking)";
const TARGET_LINE_COLOR = "var(--white-50)";
// Dimmer than CHART_TICK (--white-70) — flags a weekday with zero logged
// days directly on the axis, without needing a hover to find out. Reuses
// the same token TARGET_LINE_COLOR/the unit label already use, both
// defined in light AND dark :root (unlike --white-30/--white-40, which
// only exist under the dark media query — confirmed while picking the
// reference-line color, see that constant's own history).
const MUTED_TICK_COLOR = "var(--white-50)";
// Theme-aware, shared with the other Reports charts — see CHART_THEME
// (viewTokens.js) for the full "why".
const { tick: CHART_TICK, tickFontSize: CHART_TICK_SIZE, grid: CHART_GRID } = CHART_THEME;

// buildOverUnderSeries's weekdayIndex (1..7, locale-independent) → the
// overUnderChart.weekdayPlural.* key for that day. Position-based, not
// derived from the series' own `weekday` field (which is moment's
// locale-formatted "ddd" abbreviation — "vie" in Spanish, not matchable
// against an English-keyed lookup).
const WEEKDAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

// Per the spec: 0 decimals once the value's magnitude reaches double digits
// (kcal deltas), 1 decimal below that (gram deltas, which are usually
// single-digit and would round away to nothing at 0 decimals) — and an
// explicit "+" sign since this is the SIGNED row (unlike "% off target",
// which reads fine with only a bare "-" for under).
const fmtSignedValue = (v) => {
  const rounded = Math.abs(v) >= 20 ? Math.round(v) : Math.round(v * 10) / 10;
  return rounded >= 0 ? `+${rounded}` : `${rounded}`;
};

// A value of exactly (or very near) 0 would otherwise render as an
// invisible sliver — every weekday THAT HAS LOGGED DAYS should show SOME
// bar, even a "basically on target" one, so the coach isn't left wondering
// whether data is missing vs. genuinely perfect. Direction for the sliver
// case defaults to the "over" side (value >= 0). Weekdays with no logged
// days are excluded from this floor entirely — see OverUnderBar.
const MIN_BAR_PX = 2;

/* ---- custom tooltip -------------------------------------------------------- */

// buildOverUnderSeries always returns exactly 7 entries (one per ISO
// weekday, weekdayGroups' own guarantee — see its comment), so `pt` is
// never actually missing here; isEmpty only guards the theoretical case of
// the series prop being empty outright. n === 0 (a weekday with no logged
// days this period — or every weekday, in the whole-period-empty case) is
// its own real branch below, not something to suppress: hovering a
// dimmed/bar-less weekday should say so, not show nothing.
const OverUnderTooltip = ({ active, payload, onMeasureWidth, metricLabel, unit }) => {
  const { t } = useTranslation("athlete-portal");
  return (
    <TooltipShell active={active} payload={payload} onMeasureWidth={onMeasureWidth} isEmpty={(pt) => !pt}>
      {(pt) => (
        <>
          <div className="np-reports__tooltip-title">{pt.weekday}</div>
          {pt.n === 0 ? (
            <div className="np-reports__tooltip-label">{ro(t, "tooltipNoLoggedDays")}</div>
          ) : (
            <>
              <div className="np-hero__tooltip-row">
                <span className="np-reports__tooltip-label">
                  <span className="np-overunder-chart__tooltip-swatch" />
                  {ro(t, "tooltipAvgRow", { metric: metricLabel })}
                </span>
                <span style={{ color: BAR_COLOR }}>
                  {fmtSignedValue(pt.signedAvg)} {unit}
                </span>
              </div>
              <div className="np-hero__tooltip-row">
                <span className="np-reports__tooltip-label">{ro(t, "tooltipPctRow")}</span>
                <span style={{ color: BAR_COLOR }}>{pt.pctOff.toFixed(1)}%</span>
              </div>
              <div className="np-overunder-chart__tooltip-footer">
                {ro(t, "tooltipFooter", {
                  count: pt.n,
                  weekday: ro(t, `weekdayPlural.${WEEKDAY_KEYS[pt.weekdayIndex - 1]}`),
                })}
              </div>
            </>
          )}
        </>
      )}
    </TooltipShell>
  );
};

OverUnderTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  onMeasureWidth: PropTypes.func,
  metricLabel: PropTypes.string,
  unit: PropTypes.string,
};

/* ---- custom bar shape: rounded on the OUTER edge only ------------------- */

// Recharts (Bar.js's getRectangleData) sets `y` to the pixel position of
// the DATA VALUE itself (yAxis.scale(value)), and `height` to
// baselineScale - valueScale — i.e. SIGNED: positive for an over-target
// bar (value sits above the y=0 baseline, so y is already the bar's top
// and y+height lands exactly on the baseline), but NEGATIVE for an
// under-target bar (value sits below the baseline, so y is really the
// bar's BOTTOM and height points the "wrong" way back up to the baseline).
// `y + height` is therefore the baseline's own pixel position either way —
// that invariant is what the MIN_BAR_PX floor below anchors on, rather
// than trusting the sign of `height` (an earlier version of this comment
// assumed positive bars flip to "y is already top" / negative bars flip to
// "y is the baseline", which is backwards for the negative case — that
// wrong assumption fed straight into `Math.max(height, MIN_BAR_PX)` below:
// for every under-target bar `height` is negative, so `Math.max` against a
// negative number always returned MIN_BAR_PX regardless of the bar's real
// magnitude — every under-target bar rendered as a 2px sliver pinned to
// its true value's height instead of a full bar reaching down from
// target). Recharts also feeds this the per-frame INTERPOLATED
// x/y/width/height while the toggle's transition animation is running (see
// the Bar's animation props below), so the same logic doubles as the
// animation's own shape at every frame for free.
const OverUnderBar = (props) => {
  const { x, y, width, height, payload } = props;
  // A weekday with no logged days carries signedAvg: null. Recharts does
  // NOT skip null-valued entries once a custom `shape` is supplied — it
  // still calls this for them, with a null/0 height that the MIN_BAR_PX
  // floor below would inflate into a real 2px bar resting on the baseline.
  // That reads as "logged, and dead on target", the exact opposite of the
  // truth. Bail out before the floor applies; the dimmed axis tick and the
  // tooltip's own n === 0 branch are what mark such a day instead.
  if (payload?.n === 0) return null;
  const isOver = (payload?.signedAvg ?? 0) >= 0;
  const baselineY = y + height; // pixel-y of the "target" line — sign-independent, see comment above
  const h = Math.max(Math.abs(height), MIN_BAR_PX);
  const adjY = isOver ? baselineY - h : baselineY;
  // [top-left, top-right, bottom-right, bottom-left] — rounded only on the
  // corners at the bar's far (non-baseline) end.
  const radius = isOver ? [3, 3, 0, 0] : [0, 0, 3, 3];
  return <Rectangle x={x} y={adjY} width={width} height={h} radius={radius} fill={BAR_COLOR} />;
};

/* ---- chart ----------------------------------------------------------------- */

export const ReportsOverUnderChart = ({ dayRows, todayUtc }) => {
  const { t } = useTranslation("athlete-portal");
  const [activeMetric, setActiveMetric] = useState(OVER_UNDER_METRICS[0]); // "kcal"

  const series = useMemo(
    () => buildOverUnderSeries(dayRows, activeMetric, todayUtc),
    [dayRows, activeMetric, todayUtc]
  );

  const axisMax = useMemo(() => {
    const maxAbs = series.reduce((m, d) => Math.max(m, Math.abs(d.signedAvg ?? 0)), 0);
    return overUnderAxisMax(maxAbs);
  }, [series]);

  // Whole selected period has no logged days for this metric — every entry
  // is n:0, so no bars render at all (OverUnderBar returns null for those).
  // The axes/zero-line still render normally; only the plot
  // area gets a centered message explaining the blank space, same idea as
  // the per-weekday "No logged days" tooltip branch below.
  const isEmptyPeriod = useMemo(() => series.every((d) => !d.n), [series]);

  // Dims a weekday's own axis label when it has zero logged days, so a gap
  // in the data is visible without needing to hover to discover it. Defined
  // here (closing over `series`) rather than as a standalone component,
  // since XAxis's `tick` render prop has no way to receive extra props.
  // Recharts passes each tick's own `index` into the data array — reading
  // series[index] directly avoids matching on `payload.value` (the
  // locale-formatted weekday string), which would only ever equal that
  // same entry's own field anyway.
  const renderWeekdayTick = ({ x, y, payload, index }) => {
    const point = series[index];
    return (
      <text
        x={x}
        y={y + 12}
        textAnchor="middle"
        fontSize={CHART_TICK_SIZE}
        fill={point?.n ? CHART_TICK : MUTED_TICK_COLOR}
      >
        {payload.value}
      </text>
    );
  };

  // Manual tooltip centering — see useCenteredTooltip's own header comment
  // for the full "why" (Recharts doesn't do this by default). Closing the
  // tooltip on metric change is automatic here, not something to wire up
  // separately: activeMetric changing re-renders BarChart with new data,
  // and Recharts itself clears any active tooltip state on a data-prop
  // change (same reason none of the sibling charts need to handle this
  // explicitly either).
  const { containerRef, tooltipPosition, onMeasureWidth, chartHandlers } = useCenteredTooltip();

  return (
    <div className="np-overunder-chart">
      <div className="np-overunder-chart__header">
        <span className="np-overunder-chart__unit">{translateMacroUnit(t, activeMetric)}</span>
        <MetricToggle activeMetric={activeMetric} onChange={setActiveMetric} />
      </div>
      <div className="np-overunder-chart__plot" ref={containerRef}>
        <ResponsiveContainer width="99%" height={280}>
          <BarChart data={series} margin={{ top: 8, right: 64, bottom: 0, left: 0 }} {...chartHandlers}>
            <CartesianGrid stroke={CHART_GRID} vertical={false} />
            <XAxis dataKey="weekday" tick={renderWeekdayTick} tickLine={false} axisLine={false} />
            <YAxis
              domain={[-axisMax, axisMax]}
              tick={{ fontSize: CHART_TICK_SIZE, fill: CHART_TICK }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(v) => `${Math.round(v)}`}
              width={44}
            />
            <ReferenceLine
              y={0}
              stroke={TARGET_LINE_COLOR}
              strokeDasharray="4 2"
              label={{ value: ro(t, "targetLine"), position: "right", fill: TARGET_LINE_COLOR, fontSize: 11 }}
            />
            <Tooltip
              content={
                <OverUnderTooltip
                  onMeasureWidth={onMeasureWidth}
                  metricLabel={translateMacroLabel(t, activeMetric)}
                  unit={translateMacroUnit(t, activeMetric)}
                />
              }
              cursor={false}
              position={tooltipPosition}
            />
            {/* Unlike the sibling Reports charts (isAnimationActive=false,
                static once mounted), this bar's data changes on every toggle
                click while staying mounted — the transition is the whole
                point of the toggle, so animation is intentionally ON here.
                Recharts' animationEasing only ships named d3-ease presets,
                not arbitrary cubic-bezier() strings — "ease-in-out" is the
                closest built-in match to the spec's cubic-bezier(.4,0,.2,1). */}
            <Bar
              dataKey="signedAvg"
              shape={OverUnderBar}
              isAnimationActive
              animationDuration={350}
              animationEasing="ease-in-out"
            />
          </BarChart>
        </ResponsiveContainer>
        <span className="np-overunder-chart__label np-overunder-chart__label--over">{ro(t, "labelOver")}</span>
        <span className="np-overunder-chart__label np-overunder-chart__label--under">{ro(t, "labelUnder")}</span>
        {isEmptyPeriod && <div className="np-overunder-chart__empty-msg">{ro(t, "emptyPeriod")}</div>}
      </div>
    </div>
  );
};

ReportsOverUnderChart.propTypes = {
  dayRows: PropTypes.array.isRequired,
  todayUtc: PropTypes.number.isRequired,
};

export default ReportsOverUnderChart;
